
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { serviceCategories } from '@/data/servicesData';
import ServicesHeader from '@/components/services/ServicesHeader';
import CategoryNav from '@/components/services/CategoryNav';
import ServicesCategorySection from '@/components/services/ServicesCategorySection';
import ServicesCta from '@/components/services/ServicesCta';
import ServicesSeo from '@/components/services/ServicesSeo';

const ServicesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Get hash from URL if present
    const hash = window.location.hash.substring(1);
    if (hash) {
      setSelectedCategory(hash);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, []);

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
    
    // Update URL hash without page reload
    if (categoryId !== selectedCategory) {
      window.history.pushState(null, '', `#${categoryId}`);
    } else {
      window.history.pushState(null, '', window.location.pathname);
    }
    
    // Scroll to category section
    if (categoryId !== selectedCategory) {
      const element = document.getElementById(categoryId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  };

  // Schema for services page
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Landscaping Services",
    "description": "Professional landscaping services in Fairfield County and New Haven County, Connecticut",
    "itemListElement": serviceCategories.flatMap((category, catIndex) => 
      category.services.map((service, svcIndex) => ({
        "@type": "ListItem",
        "position": catIndex * 10 + svcIndex + 1,
        "item": {
          "@type": "Service",
          "name": service.title,
          "description": service.description,
          "provider": {
            "@type": "LandscapingBusiness",
            "name": "PK Landscape Associates"
          }
        }
      }))
    )
  };
  
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Landscaping Services CT | Lawn Care, Design & Hardscaping | Fairfield County</title>
        <meta 
          name="description" 
          content="Professional landscaping services in Fairfield & New Haven County, CT. Lawn maintenance, landscape design, hardscaping, irrigation & snow removal. Serving Westport, Darien, Fairfield & more." 
        />
        <link rel="canonical" href="https://pklandscapect.com/services" />
        <meta property="og:title" content="Landscaping Services | PK Landscape Associates" />
        <meta property="og:description" content="Comprehensive landscaping services for residential and commercial properties across Connecticut." />
        <meta property="og:url" content="https://pklandscapect.com/services" />
        <script type="application/ld+json">{JSON.stringify(servicesSchema)}</script>
      </Helmet>

      <Navbar />
      
      {/* Header */}
      <ServicesHeader />
      
      {/* Service Categories Navigation */}
      <CategoryNav 
        categories={serviceCategories.map(cat => ({ id: cat.id, name: cat.name }))}
        selectedCategory={selectedCategory}
        onCategorySelect={handleCategorySelect}
      />
      
      {/* Services List */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {serviceCategories.map((category) => (
            <ServicesCategorySection key={category.id} category={category} />
          ))}
        </div>
      </section>
      
      {/* CTA Section */}
      <ServicesCta />
      
      {/* SEO Content with Location Links */}
      <ServicesSeo />
      
      <Footer />
    </div>
  );
};

export default ServicesPage;
